import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { CheckCircle2, XCircle, Undo2, Activity, ChevronRight } from 'lucide-react';

/**
 * Recent Mira Actions widget — last 10 writes Mira performed for THIS user
 * (or all users for the CEO). Verifies what Mira ACTUALLY did against what
 * she SAID she did. Glanceable, no clicks needed.
 *
 * Pulls from GET /api/operator-mira/recent-actions
 */
export default function RecentMiraActions({ limit = 10, autoRefreshMs = 60000, compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(!compact);

  const load = async () => {
    try {
      const r = await api.get(`/operator-mira/recent-actions?limit=${limit}`);
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    if (autoRefreshMs > 0) {
      const t = setInterval(load, autoRefreshMs);
      return () => clearInterval(t);
    }
  }, [limit, autoRefreshMs]);

  const fmtTime = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    const now = new Date();
    const diffSec = Math.floor((now - d) / 1000);
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  const StatusIcon = ({ status, undone, cancelled }) => {
    if (cancelled) return <XCircle className="h-3.5 w-3.5" style={{ color: '#9CA3AF' }} title="cancelled" />;
    if (undone) return <Undo2 className="h-3.5 w-3.5" style={{ color: '#A32D2D' }} title="undone" />;
    if (status === 'ok') return <CheckCircle2 className="h-3.5 w-3.5" style={{ color: '#2D8B5E' }} title="success" />;
    return <XCircle className="h-3.5 w-3.5" style={{ color: '#A32D2D' }} title="failed" />;
  };

  return (
    <div className="bg-white rounded-lg border" style={{ borderColor: '#E5E1DC' }} data-testid="recent-mira-actions">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
      >
        <span className="flex items-center gap-2">
          <Activity className="h-4 w-4" style={{ color: '#3B5BA9' }} />
          <span className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>
            Recent Mira Actions
          </span>
          {items.length > 0 && (
            <span className="text-[11px] px-1.5 py-0.5 rounded-full" style={{ background: '#F3F4F6', color: '#6B7280' }}>
              {items.length}
            </span>
          )}
        </span>
        <ChevronRight
          className="h-4 w-4 transition-transform"
          style={{ color: '#6B7280', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
        />
      </button>

      {expanded && (
        <div className="border-t px-4 py-3" style={{ borderColor: '#F3F4F6' }}>
          {loading && <div className="text-[12px] italic" style={{ color: '#9CA3AF' }}>Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="text-[12px] italic" style={{ color: '#9CA3AF' }}>
              No recent Mira actions yet. Ask Mira to add a deal, log a sale, or update a record.
            </div>
          )}
          <ul className="divide-y" style={{ borderColor: '#F3F4F6' }}>
            {items.map((it) => (
              <li key={it.id} className="py-2 flex items-start gap-2" data-testid="mira-action-row">
                <span className="mt-0.5 flex-shrink-0">
                  <StatusIcon status={it.status} undone={it.undone} cancelled={it.cancelled} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[12px] font-mono" style={{ color: '#374151' }}>
                    {it.summary || `${it.intent} → ${it.entity_type}`}
                  </span>
                  <span className="block text-[10px]" style={{ color: '#9CA3AF' }}>
                    {fmtTime(it.timestamp)}
                    {it.division_id && (
                      <span className="ml-1.5 px-1 py-0.5 rounded" style={{ background: '#F3F4F6', color: '#6B7280' }}>
                        {it.division_id}
                      </span>
                    )}
                    {it.user && it.user !== '—' && <span className="ml-1.5">by {it.user}</span>}
                  </span>
                  {it.command && (
                    <span className="block text-[10px] italic mt-0.5 truncate" style={{ color: '#9CA3AF' }} title={it.command}>
                      "{it.command}"
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
